import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ShoppingBasket } from "lucide-react";
import { rgApi, EXTRA_NEEDS, fmtLongDate, fmtShortDate, firstName } from "@/lib/rgApi";
import ShoppingListModal from "./ShoppingListModal";
import CleaningModal from "./CleaningModal";
import { parseBreakfastForm } from "./rgBreakfastParser";

/**
 * Today — Cockpit "Aujourd'hui au refuge".
 *
 * Règles clés :
 *  • Seules les actions du JOUR sont affichées (le futur reste dans Calendrier).
 *  • Une action terminée disparaît de la vue (persistée en BD et reflétée dans la fiche voyageur).
 *  • Chaque arrivée du jour = ligne simple (prénom · arrive aujourd'hui · X voyageurs · canal)
 *    suivie d'un bloc "Particularités" listant ce qui modifie la préparation.
 *  • La ligne Ménage affiche une petite ✨ si Bloc 0 ou Bloc ✨ ont été avancés.
 */

// Mapping des canaux → libellé propre
function channelLabel(source) {
  const s = (source || "").toLowerCase().trim();
  if (!s) return "Direct";
  if (s.includes("airbnb")) return "Airbnb";
  if (s.includes("booking")) return "Booking";
  if (s.includes("lodgify") || s.includes("manual")) return "Lodgify";
  if (s.includes("direct")) return "Direct";
  return source;
}

// Construit la liste des particularités qui modifient la préparation
function buildParticularites(r) {
  const items = [];
  const extras = r.extras || [];
  const cat = r.third_guest_category;
  const decor = r.decor_type;

  if (extras.includes("decor")) items.push({ icon: "🌹", label: "Décor" });
  if (decor === "nuit_rouge") items.push({ icon: "🌹", label: "Nuit Rouge" });
  if (decor === "nuit_rose") items.push({ icon: "🌸", label: "Nuit Rose" });
  if (cat === "enfant") items.push({ icon: "🧒", label: "Enfant" });
  if (cat === "bebe") items.push({ icon: "👶", label: "Bébé" });
  if (r.troisieme_lit_type === "parapluie") items.push({ icon: "🛏️", label: "Lit parapluie" });
  else if (extras.includes("troisieme_lit") || (r.people || 0) >= 3) {
    items.push({ icon: "🛏️", label: "Couchage supplémentaire" });
  }
  if (extras.includes("bain_fleurs")) items.push({ icon: "🌸", label: "Bain de fleurs" });
  if (extras.includes("petit_dejeuner")) items.push({ icon: "🥐", label: "Petit déjeuner" });
  // Autre particularité utile : notes non vides (aperçu)
  const notes = (r.notes || "").trim();
  if (notes) items.push({ icon: "📝", label: "Note", tip: notes });
  // Expérience libre (module_states.experiences éventuelle)
  const ms = r.module_states || {};
  if (ms.experience || ms.experiences) items.push({ icon: "✨", label: "Expérience" });
  return items;
}

export default function Today() {
  const [data, setData] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [busy, setBusy] = useState(false);
  const [shopResa, setShopResa] = useState(null);
  const [cleaningRes, setCleaningRes] = useState(null); // {reservation, cleaningId, done}
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const [d, resas] = await Promise.all([rgApi.today(), rgApi.listReservations(true)]);
      setData(d);
      setUpcoming(Array.isArray(resas) ? resas : []);
    } catch (e) {
      if (e.status === 401) nav("/admin");
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const toggleAction = async (actionId, currentDone) => {
    setBusy(true);
    try {
      await rgApi.setCompletion(actionId, !currentDone);
      await load();
    } finally { setBusy(false); }
  };

  const openCleaning = async (c) => {
    try {
      const reservation = await rgApi.getReservation(c.reservation_id);
      setCleaningRes({ reservation, cleaningId: c.id, done: !!c.done });
    } catch (_e) { /* noop */ }
  };

  const refreshCleaning = async () => {
    if (!cleaningRes) return;
    try {
      const updated = await rgApi.getReservation(cleaningRes.reservation.id);
      setCleaningRes((p) => (p ? { ...p, reservation: updated } : p));
    } catch (_e) { /* noop */ }
    await load();
  };

  const updateCleaningReservation = async (patchData) => {
    if (!cleaningRes) return;
    if (patchData && Object.keys(patchData).length > 0) {
      await rgApi.updateReservation(cleaningRes.reservation.id, patchData);
    }
    await refreshCleaning();
  };

  if (!data) return <div className="rg-page rg-loading">Chargement…</div>;

  const day = data.today;

  return (
    <div className="rg-page" data-testid="rg-today-page">
      <FormsToDoBanner reservations={upcoming} todayIso={day.date} onOpen={(id) => nav(`/admin/gestion/voyageur/${id}`)} />

      <div className="rg-section" data-testid="rg-day-section">
        <h3>Aujourd&apos;hui · {fmtLongDate(day.date)}</h3>

        {/* Arrivées du jour */}
        {(day.arrivals || []).map((r) => (
          <ArrivalCard key={`arr-${r.id}`} r={r} />
        ))}

        {/* Actions non terminées uniquement */}
        <ActionRows
          day={day}
          onToggle={toggleAction}
          busy={busy}
          onOpenShop={setShopResa}
          onOpenCleaning={openCleaning}
        />

        {(day.arrivals || []).length === 0 && !hasAnyPending(day) && (
          <div className="rg-card rg-muted" style={{ textAlign: "center" }}>
            Tout est fait pour aujourd&apos;hui.
          </div>
        )}
      </div>

      {data.next && (
        <div className="rg-card" data-testid="rg-next-arrival">
          <p className="rg-tiny">Prochaine arrivée</p>
          <p className="rg-h3" style={{ marginTop: 4 }}>{firstName(data.next.name)}</p>
          <p className="rg-muted">{fmtLongDate(data.next.date_arrival)} · {data.next.nights} nuits</p>
        </div>
      )}

      <ShoppingListModal open={!!shopResa} onClose={() => setShopResa(null)} reservation={shopResa} />
      <CleaningModal
        open={!!cleaningRes}
        onClose={() => setCleaningRes(null)}
        reservation={cleaningRes?.reservation}
        cleaningId={cleaningRes?.cleaningId}
        done={cleaningRes?.done}
        onDone={refreshCleaning}
        onUpdateReservation={updateCleaningReservation}
      />
    </div>
  );
}

function hasAnyPending(day) {
  const lists = [day.actions, day.cleaning, day.breakfasts, day.experiences, day.prep_fleurs];
  return lists.some((l) => (l || []).some((x) => !x.done));
}

// Formulaires en attente pour une réservation (couchage / petit-déj / bain fleuri)
function pendingForms(r) {
  const extras = r.extras || [];
  const fs = r.form_status || {};
  const out = [];
  const needs3 = (r.people || 0) >= 3 || !!r.extra_guest_added;
  if (needs3 && fs.couchage !== "received") out.push({ icon: "🛏️", label: "Couchage", sent: fs.couchage === "sent" });
  if (extras.includes("petit_dejeuner")) {
    const bf = r.breakfast_form || {};
    const received = bf.received === true || r.petit_dejeuner_confirmed === true || parseBreakfastForm(r.notes).ok || fs.petit_dejeuner === "received";
    if (!received) out.push({ icon: "🥐", label: "Petit-déj", sent: fs.petit_dejeuner === "sent" });
  }
  if (extras.includes("bain_fleurs") && fs.bain_fleurs !== "received") out.push({ icon: "🌸", label: "Bain fleuri", sent: fs.bain_fleurs === "sent" });
  return out;
}

// Bandeau « À préparer avant l'arrivée » — arrivées des 7 prochains jours avec formulaires en attente
function FormsToDoBanner({ reservations, todayIso, onOpen }) {
  const t0 = new Date(todayIso + "T12:00:00").getTime();
  const rows = (reservations || [])
    .map((r) => {
      const days = Math.round((new Date(r.date_arrival + "T12:00:00").getTime() - t0) / 86400000);
      return { r, days, forms: pendingForms(r) };
    })
    .filter((x) => x.days >= 0 && x.days <= 7 && x.forms.length > 0)
    .sort((a, b) => a.days - b.days);

  if (rows.length === 0) return null;

  return (
    <div className="rg-section" data-testid="rg-forms-todo-banner">
      <h3>📋 À préparer avant l&apos;arrivée · 7 prochains jours</h3>
      <p className="rg-muted" style={{ marginTop: -4, marginBottom: 10, fontSize: 12 }}>
        Formulaires encore à envoyer ou en attente de réponse. Rien ne part automatiquement — ouvre la fiche pour déclencher l&apos;envoi.
      </p>
      {rows.map(({ r, days, forms }) => (
        <div key={r.id} className="rg-card" data-testid={`rg-todo-${r.id}`}
          style={{ marginBottom: 8, padding: 12, display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>
              {firstName(r.name)}{" "}
              <span className="rg-muted" style={{ fontWeight: 400 }}>· {fmtShortDate(r.date_arrival)} · {days === 0 ? "aujourd'hui" : `J-${days}`}</span>
            </div>
            <div className="rg-row" style={{ gap: 6, flexWrap: "wrap", marginTop: 6 }}>
              {forms.map((f, i) => (
                <span key={i} data-testid={`rg-todo-${r.id}-${f.label}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: "3px 8px", borderRadius: 999,
                    background: f.sent ? "#fff3cd" : "var(--rg-border-soft)", color: f.sent ? "#8a6d1a" : "var(--rg-text-2)",
                  }}>
                  {f.icon} {f.label} {f.sent ? "· en attente ❓" : "· à envoyer"}
                </span>
              ))}
            </div>
          </div>
          <button className="rg-btn small" onClick={() => onOpen(r.id)} data-testid={`rg-todo-open-${r.id}`}>
            Ouvrir la fiche
          </button>
        </div>
      ))}
    </div>
  );
}

function ArrivalCard({ r }) {
  const parts = buildParticularites(r);
  return (
    <div className="rg-event" data-testid={`rg-arrival-${r.id}`} style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
      <div className="rg-row" style={{ gap: 10 }}>
        <span className="rg-event-icon">📍</span>
        <div className="rg-event-body">
          <div className="rg-event-label">
            {firstName(r.name)} <span className="rg-muted" style={{ fontWeight: 400 }}>· arrive aujourd&apos;hui</span>
          </div>
          <div className="rg-event-name">
            {r.people || 1} voyageur{(r.people || 1) > 1 ? "s" : ""} · {channelLabel(r.source)}
          </div>
        </div>
      </div>
      {parts.length > 0 && (
        <div
          data-testid={`rg-particularites-${r.id}`}
          style={{
            display: "flex", flexWrap: "wrap", gap: 6,
            paddingLeft: 42, marginTop: 2,
          }}
        >
          {parts.map((p, i) => (
            <span
              key={i}
              className="rg-chip"
              title={p.tip || p.label}
              data-testid={`rg-partic-${r.id}-${i}`}
            >
              <span>{p.icon}</span> {p.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtDur(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s - h * 3600) / 60);
  if (h > 0 && m > 0) return `${h} h ${String(m).padStart(2, "0")}`;
  if (h > 0) return `${h} h`;
  return `${m} min`;
}

function ActionRows({ day, onToggle, busy, onOpenShop, onOpenCleaning }) {
  const rows = buildRows(day).filter((r) => !r.done);
  return rows.map((r) => (
    <div key={r.id}>
      <div className={`rg-event ${r.done ? "done" : ""}`} data-testid={`rg-row-${r.id}`}>
        <span className="rg-event-icon">{r.icon}</span>
        <div className="rg-event-body">
          <div className="rg-event-label">
            {r.label}
            {r.starBadge ? <span data-testid={`rg-star-${r.id}`} style={{ marginLeft: 6 }} title="Bloc avancé par Eva">✨</span> : null}
            {r.extraIcons ? <span style={{ marginLeft: 8 }}>{r.extraIcons}</span> : null}
            {r.time ? <span className="rg-muted" style={{ marginLeft: 8, fontSize: 12 }}>· {r.time}</span> : null}
          </div>
          <div className="rg-event-name">— {r.name}</div>
          {r.subNote && <div className="rg-event-sub">{r.subNote}</div>}
        </div>
        {r.showShop && r.reservation && (
          <button
            type="button"
            onClick={() => onOpenShop(r.reservation)}
            className="rg-btn ghost small"
            data-testid={`rg-shop-${r.id}`}
            title="Liste de courses">
            <ShoppingBasket size={13} />
          </button>
        )}
        {r.isCleaning && r.raw && (
          <button
            type="button"
            onClick={() => onOpenCleaning(r.raw)}
            className="rg-btn ghost small"
            data-testid={`rg-open-cleaning-${r.id}`}
            title="Ouvrir la préparation ménage">
            Ouvrir
          </button>
        )}
        {r.hasToggle && !r.isCleaning && (
          <button
            type="button"
            onClick={() => onToggle(r.id, r.done)}
            disabled={busy}
            className={`rg-btn small ${r.done ? "done" : "ghost"}`}
            data-testid={`rg-toggle-${r.id}`}
          >
            {r.done ? <><Check size={12} /> Fait</> : "Fait"}
          </button>
        )}
      </div>
      {r.isCleaning && (r.anticipated > 0 || r.remaining > 0) && (
        <MenageDashboard
          totalTarget={r.totalTarget}
          anticipated={r.anticipated}
          todayWork={r.todayWork}
          remaining={r.remaining}
          extraTargets={r.extraTargets}
          advanceDone={r.advanceDone}
          rowId={r.id}
        />
      )}
    </div>
  ));
}

function fmtEtaHHhMM(sec) {
  // Heure de fin = maintenant + sec (heure locale Réunion)
  const now = new Date();
  const eta = new Date(now.getTime() + Math.max(0, sec) * 1000);
  const h = String(eta.getHours()).padStart(2, "0");
  const m = String(eta.getMinutes()).padStart(2, "0");
  return `${h}h${m}`;
}

function MenageDashboard({ totalTarget, anticipated, todayWork, remaining, extraTargets, advanceDone, rowId }) {
  // Tick chaque minute pour rafraîchir l'heure de fin estimée
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((v) => v + 1), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  const decorSec = (extraTargets || {}).decor || 0;
  const bainSec = (extraTargets || {}).bain_fleurs || 0;
  const childBedSec = (extraTargets || {}).child_bed || 0;
  const hasSupplement = decorSec > 0 || bainSec > 0 || childBedSec > 0;

  return (
    <div
      data-testid={`rg-menage-dashboard-${rowId}`}
      style={{
        marginTop: -4,
        marginBottom: 10,
        marginLeft: 42,
        padding: "12px 14px",
        borderRadius: 10,
        background: "var(--rg-primary-light)",
        border: "1px solid var(--rg-border-soft)",
      }}
    >
      <div style={{ fontWeight: 600, color: "var(--rg-primary)", fontSize: 13, marginBottom: 8 }}>
        Ménage du jour
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", fontSize: 12, color: "var(--rg-text)" }}>
        <span>⏱️ Objectif total : <strong data-testid={`rg-dash-total-${rowId}`}>{fmtDur(totalTarget)}</strong></span>
        <span>🏁 Fin estimée : <strong data-testid={`rg-dash-eta-${rowId}`}>{remaining > 0 ? fmtEtaHHhMM(remaining) : "—"}</strong></span>
        <span>✨ Déjà anticipé : <strong data-testid={`rg-dash-anticipe-${rowId}`}>{fmtDur(anticipated)}</strong></span>
        <span>✅ Effectué aujourd&apos;hui : <strong data-testid={`rg-dash-today-${rowId}`}>{fmtDur(todayWork)}</strong></span>
        <span style={{ gridColumn: "1 / -1" }}>
          ⏳ Travail restant estimé : <strong data-testid={`rg-dash-remaining-${rowId}`}>{fmtDur(remaining)}</strong>
        </span>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11, color: "var(--rg-text-2)", marginTop: 6 }}>
        <span data-testid={`rg-antic-bloc0-${rowId}`}>Bloc 0 {advanceDone.bloc0 ? "✅" : "⏳"}</span>
        <span data-testid={`rg-antic-defroissage-${rowId}`}>Bloc ✨ {advanceDone.defroissage ? "✅" : "⏳"}</span>
      </div>
      {hasSupplement && (
        <div style={{ marginTop: 6, fontSize: 11, color: "var(--rg-text-3)", fontStyle: "italic" }}>
          Suppléments inclus dans l&apos;objectif :
          {decorSec > 0 && <span data-testid={`rg-antic-decor-${rowId}`}> 🌹 Décor ≈ {fmtDur(decorSec)}</span>}
          {bainSec > 0 && <span data-testid={`rg-antic-bain-${rowId}`}>{decorSec > 0 ? " · " : " "}🌸 Bain de fleurs ≈ {fmtDur(bainSec)}</span>}
          {childBedSec > 0 && <span data-testid={`rg-antic-child-${rowId}`}>{(decorSec > 0 || bainSec > 0) ? " · " : " "}🛏️ Couchage enfant ≈ {fmtDur(childBedSec)}</span>}
        </div>
      )}
    </div>
  );
}

function buildRows(day) {
  if (!day) return [];
  const rows = [];
  (day.actions || []).forEach((a) => {
    const extrasList = a.extras || [];
    const needsList = extrasList.map((e) => EXTRA_NEEDS[e]).filter(Boolean);
    rows.push({
      id: a.id,
      icon: "🧺",
      label: a.label || "Marché",
      time: a.time,
      name: firstName(a.guest_name),
      subNote: needsList.length > 0 ? `Prévoir : ${needsList.join(" · ")}` : a.needs,
      done: !!a.done,
      hasToggle: true,
      showShop: true,
      reservation: a.reservation || null,
    });
  });
  (day.prep_fleurs || []).forEach((p) => rows.push({
    id: p.id,
    icon: "💧",
    label: "Préparation fleurs",
    name: firstName(p.guest_name),
    done: !!p.done,
    hasToggle: true,
  }));
  (day.cleaning || []).forEach((c) => rows.push({
    id: c.id,
    icon: "🧹",
    label: "Ménage",
    time: c.time,
    name: firstName(c.guest_name),
    done: !!c.done,
    hasToggle: true,
    isCleaning: true,
    starBadge: !!c.has_star,
    raw: c,
    anticipated: c.anticipated_saved_sec || 0,
    todayWork: c.today_work_sec || 0,
    remaining: c.remaining_target_sec || 0,
    totalTarget: c.total_target_sec || 0,
    advanceDone: c.advance_done || {},
    extraTargets: c.extra_targets || {},
    applicable: c.applicable || [],
  }));
  (day.breakfasts || []).forEach((b) => rows.push({
    id: b.id,
    icon: "🥐",
    label: "Petit déjeuner",
    time: b.time,
    name: firstName(b.guest_name),
    subNote: b.details || (b.pending ? "Formulaire en attente ❓" : null),
    done: !!b.done,
    hasToggle: true,
  }));
  (day.experiences || []).forEach((x) => rows.push({
    id: x.id,
    icon: x.icon || "✨",
    label: x.label || "Expérience",
    time: x.time,
    name: firstName(x.guest_name),
    done: !!x.done,
    hasToggle: true,
  }));
  // Ligne "Arrivée" en tant qu'action générique n'est plus nécessaire : ArrivalCard s'en charge.
  return rows;
}

// (Note) L'import EXTRA_ICONS n'est plus nécessaire ici : les icônes des particularités
// sont définies inline dans buildParticularites().
